import {
  resolveUploadThumbSrc,
  toCanonicalUploadsPath,
} from '@/shein-platform/modules/products/components/publishImageUploadThumbSrc';

function isEphemeralUrl(url) {
  const u = String(url || '').trim().toLowerCase();
  return u.startsWith('blob:') || u.startsWith('data:');
}

const TEST_CDN_SIGNS = ['imgdeal-test', '-test01.', 'sheincorp.cn'];

function isTestCdnUrl(url) {
  const u = String(url || '').trim().toLowerCase();
  return TEST_CDN_SIGNS.some((sign) => u.includes(sign));
}

/**
 * 槽位里仍可落库的地址：排除同页 blob/data 与测网 CDN。
 * 有它就说明图片确实落过盘，不能因为「解析不出 canonical /uploads」就把整条清掉。
 */
function firstStableStoredUrl(img) {
  const candidates = [
    img?.ipeUrl,
    img?.publicUrl,
    img?.url,
    img?.previewUrl,
    img?.sheinUrl,
  ];
  for (const raw of candidates) {
    const url = String(raw || '').trim();
    if (!url || isEphemeralUrl(url) || isTestCdnUrl(url)) continue;
    return url;
  }
  return '';
}

/**
 * 单槽位规范化：
 * - 绝对 localhost:端口/uploads → canonical /uploads/…
 * - 失效 blob/data 一律换成 durable（回填后不能再喂灰块）
 * - 仅测网 CDN / 无法预览 → null
 */
function normalizeImageSlot(img) {
  if (!img || typeof img !== 'object') return img;
  const durable = toCanonicalUploadsPath(img.ipeUrl)
    || toCanonicalUploadsPath(img.publicUrl)
    || toCanonicalUploadsPath(img.url)
    || toCanonicalUploadsPath(img.previewUrl)
    || '';
  if (!durable) {
    const stable = firstStableStoredUrl(img);
    if (stable) {
      // 解析不出 canonical（OSS 前缀域名 / 签名地址等）也必须保留：清掉等于用户上传的图丢了
      return {
        ...img,
        previewUrl: isEphemeralUrl(img.previewUrl) ? stable : img.previewUrl,
      };
    }
    // 仅测网 CDN / 失效地址：清掉，避免灰块/裂图占槽
    if (!resolveUploadThumbSrc(img)) return null;
    const preview = String(img.previewUrl || '').trim();
    if (isEphemeralUrl(preview)) {
      // 无 durable 的死 blob：清空
      return null;
    }
    return img;
  }
  const sheinUrl = String(img.sheinUrl || '').trim();
  return {
    ...img,
    ipeUrl: durable,
    publicUrl: durable,
    url: durable,
    // 禁止把 blob:/data: 写回表单：刷新或隔日打开必灰
    previewUrl: durable,
    ...(sheinUrl ? { sheinUrl } : {}),
  };
}

function mapImageArray(list) {
  if (!Array.isArray(list)) return { next: list, changed: false };
  let changed = false;
  const next = list.map((img) => {
    const n = normalizeImageSlot(img);
    if (n !== img) changed = true;
    return n;
  }).filter(Boolean);
  if (next.length !== list.length) changed = true;
  return { next, changed };
}

function normalizeShowroomSlots(slots) {
  if (!slots || typeof slots !== 'object') {
    return { next: slots, changed: false };
  }
  let changed = false;
  const next = { ...slots };
  Object.keys(slots).forEach((key) => {
    const n = normalizeImageSlot(slots[key]);
    if (n !== slots[key]) {
      changed = true;
      next[key] = n;
    }
  });
  return { next, changed };
}

/**
 * 编辑回填 / 持久化前统一清洗图片槽：
 * - 绝对 localhost:端口/uploads → canonical /uploads/…
 * - 去掉失效 blob/data
 * - 无法预览的测网空壳 → null（显示点击上传）
 */
export function sanitizeSheinFormImageSlots(form) {
  if (!form || typeof form !== 'object') return form;
  const skcList = Array.isArray(form.skcList) ? form.skcList : null;
  const printImages = Array.isArray(form.printImages) ? form.printImages : null;
  if (!skcList && !printImages) return form;

  let changed = false;
  let nextPrint = printImages;
  if (printImages) {
    const mapped = mapImageArray(printImages);
    nextPrint = mapped.next;
    if (mapped.changed) changed = true;
  }

  let nextSkcList = skcList;
  if (skcList) {
    nextSkcList = skcList.map((skc) => {
      if (!skc || typeof skc !== 'object') return skc;
      let skcChanged = false;
      let next = skc;

      const mainImage = normalizeImageSlot(skc.mainImage);
      if (mainImage !== skc.mainImage) {
        skcChanged = true;
        next = { ...next, mainImage };
      }

      const squareImage = normalizeImageSlot(skc.squareImage);
      if (squareImage !== skc.squareImage) {
        skcChanged = true;
        next = { ...next, squareImage };
      }

      const colorBlockImage = normalizeImageSlot(skc.colorBlockImage);
      if (colorBlockImage !== skc.colorBlockImage) {
        skcChanged = true;
        next = { ...next, colorBlockImage };
      }

      if (Array.isArray(skc.detailImages)) {
        const mapped = mapImageArray(skc.detailImages);
        if (mapped.changed) {
          skcChanged = true;
          next = { ...next, detailImages: mapped.next };
        }
      }

      const showroom = normalizeShowroomSlots(skc.showroomSlots);
      if (showroom.changed) {
        skcChanged = true;
        next = { ...next, showroomSlots: showroom.next };
      }

      if (Array.isArray(skc.skuRows)) {
        const skuRows = skc.skuRows.map((sku) => {
          if (!sku || !sku.skuImage) return sku;
          const skuImage = normalizeImageSlot(sku.skuImage);
          if (skuImage === sku.skuImage) return sku;
          skcChanged = true;
          return { ...sku, skuImage };
        });
        if (skcChanged) next = { ...next, skuRows };
      }

      if (skcChanged) changed = true;
      return next;
    });
  }

  if (!changed) return form;
  return {
    ...form,
    ...(nextPrint ? { printImages: nextPrint } : {}),
    ...(nextSkcList ? { skcList: nextSkcList } : {}),
  };
}

/** 模板/草稿落库：与 sanitize 同规则，显式命名强调「禁止持久化 blob」。 */
export function persistSheinFormImageSlots(form) {
  return sanitizeSheinFormImageSlots(form);
}
